import { useState } from 'react';
import { 
  History, 
  Search, 
  Filter, 
  Calendar, 
  User, 
  Database, 
  ArrowRight,
  ChevronDown,
  Clock,
  CheckCircle2,
  Settings,
  Shield,
  Zap,
  Box,
  FileText,
  DollarSign
} from 'lucide-react';
import { useAuditLogs } from '@/api/queries';
import LoadingState from '../ui/LoadingState';
import ErrorState from '../ui/ErrorState';
import EmptyState from '../ui/EmptyState';

interface AuditoriaTabProps {
  workspaceId?: string;
}

export default function AuditoriaTab({ workspaceId }: AuditoriaTabProps) {
  const [search, setSearch] = useState('');
  const { data: logs = [], isLoading, error, refetch } = useAuditLogs(workspaceId || '');

  const getActionIcon = (action: string, table: string) => {
    if (action === 'CREATE') return <Zap size={14} className="text-emerald-500" />;
    if (action === 'DELETE') return <XCircleIcon size={14} className="text-rose-500" />;
    if (action === 'INVITE') return <User size={14} className="text-blue-500" />;
    if (table === 'finance_entries' || table === 'Invoice' || table === 'Transaction') return <DollarSign size={14} className="text-green-500" />;
    if (table === 'Project') return <Box size={14} className="text-indigo-500" />;
    if (table === 'Document') return <FileText size={14} className="text-amber-500" />;
    return <Settings size={14} className="text-slate-500" />;
  };

  const getTableLabel = (table: string) => {
    const map: any = {
      'workspaces': 'Workspace',
      'workspace_members': 'Membro',
      'workspace_invitations': 'Convite',
      'Project': 'Projeto',
      'Product': 'Produto',
      'Invoice': 'Financeiro',
      'Transaction': 'Transação',
      'Task': 'Tarefa',
      'Document': 'Documento',
      'Company': 'Empresa',
      'Customer': 'Cliente',
      'OrganizationMember': 'Membro',
      'Invitation': 'Convite',
    };
    return map[table] || table;
  };

  const filteredLogs = (Array.isArray(logs) ? logs : []).filter((log: any) => 
    (log.actorName || log.actorId || '').toLowerCase().includes(search.toLowerCase()) || 
    (log.operation || log.action || '').toLowerCase().includes(search.toLowerCase()) ||
    (log.tableName || '').toLowerCase().includes(search.toLowerCase())
  );

  if (!workspaceId) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-[#64748B] font-medium">
        Selecione um workspace para visualizar a auditoria.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 h-full">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white border border-[#0F172A0A] rounded-[24px] p-4 shadow-[0_4px_20px_rgb(0,0,0,0.01)]">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
          <input 
            type="text" 
            placeholder="Filtrar por usuário, ação ou recurso..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-[#FAFAFB] border-none rounded-xl py-2.5 pl-11 pr-4 text-sm font-medium focus:ring-2 focus:ring-black/5 transition-all outline-none text-[#111111]"
          />
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 bg-[#FAFAFB] border border-[#0F172A0A] text-[#64748B] px-4 py-2.5 rounded-xl text-sm font-bold hover:text-[#111111] transition-all">
            <Calendar size={16} /> Este Mês <ChevronDown size={14} />
          </button>
          <button className="flex items-center gap-2 bg-[#FAFAFB] border border-[#0F172A0A] text-[#64748B] px-4 py-2.5 rounded-xl text-sm font-bold hover:text-[#111111] transition-all">
            <Filter size={16} /> Filtros <ChevronDown size={14} />
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#0F172A0A] rounded-[32px] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.02)] flex-1 overflow-y-auto no-scrollbar">
        {isLoading ? (
          <LoadingState message="Carregando auditoria..." />
      ) : error ? (
        <ErrorState message={error.message || 'Erro ao carregar auditoria'} retry={() => refetch()} />
        ) : filteredLogs.length === 0 ? (
          <EmptyState title="Nenhum registro encontrado" description="Não há logs de auditoria para este workspace." />
        ) : (
          <div className="flex flex-col gap-8 relative">
            <div className="absolute left-[23px] top-4 bottom-4 w-px bg-gradient-to-b from-[#0F172A0A] via-[#0F172A0A] to-transparent" />

            {filteredLogs.map((log: any) => (
              <div key={log.id} className="flex gap-6 relative group">
                <div className="w-12 h-12 rounded-2xl bg-white border border-[#0F172A0A] flex items-center justify-center text-[#111111] z-10 shadow-sm group-hover:scale-110 group-hover:shadow-md transition-all">
                  {getActionIcon(log.operation || log.action, log.tableName)}
                </div>

                <div className="flex-1 flex flex-col gap-2 pt-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-[#111111]">{log.actorName || log.actorId?.slice(0, 8) || 'Sistema'}</span>
                      <span className="text-[10px] font-bold text-[#64748B] bg-[#FAFAFB] border border-[#0F172A05] px-2 py-0.5 rounded-md uppercase tracking-widest">{log.operation || log.action}</span>
                      <ArrowRight size={12} className="text-[#94A3B8]" />
                      <span className="text-sm font-bold text-[#111111]">{getTableLabel(log.tableName)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs font-medium text-[#94A3B8]">
                      <Clock size={12} />
                      {new Date(log.changedAt || log.createdAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <p className="text-[13px] text-[#64748B] font-medium leading-relaxed">
                      Ação realizada no registro <span className="text-[#111111] font-bold">#{log.recordId?.slice(0, 8) || log.id?.slice(0, 8)}</span> da tabela <span className="font-bold">{log.tableName}</span>.
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function XCircleIcon(props: any) {
  return <History {...props} />;
}
